import type {
  IconButtonProps as MuiIconButtonProps,
  SvgIconProps,
} from '@mui/material'
import { IconButton as MuiIconButton, styled } from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import React from 'react'

export type IconButtonColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'default'
export type IconButtonVariant = 'contained' | 'outlined' | 'text'
export type IconButtonSize = 'small' | 'medium' | 'large'

export interface EnhancedIconButtonProps
  extends Omit<MuiIconButtonProps, 'color'> {
  color?: IconButtonColor
  variant?: IconButtonVariant
  size?: IconButtonSize
  icon?: React.ComponentType<SvgIconProps>
  iconProps?: SvgIconProps
}

const getButtonSizeStyles = (size: IconButtonSize = 'medium') => {
  const sizeMap = {
    small: {
      padding: 8,
      '& svg': { fontSize: 20 },
    },
    medium: {
      padding: 12,
      '& svg': { fontSize: 24 },
    },
    large: {
      padding: 16,
      '& svg': { fontSize: 28 },
    },
  }
  return sizeMap[size]
}

const getColorStyles = (
  theme: Theme,
  color: IconButtonColor = 'default',
  variant: IconButtonVariant = 'text',
) => {
  const isDefaultColor = color === 'default'
  const baseColor = isDefaultColor
    ? theme.palette.grey[300]
    : theme.palette[color].main
  const textColor = isDefaultColor
    ? theme.palette.text.primary
    : theme.palette[color].main

  const variantStyles = {
    contained: {
      backgroundColor: baseColor,
      color: isDefaultColor
        ? theme.palette.text.primary
        : theme.palette.common.white,
      boxShadow: theme.shadows[2],
      '&:hover': {
        backgroundColor: isDefaultColor
          ? theme.palette.grey[400]
          : theme.palette[color].dark,
        boxShadow: theme.shadows[5],
      },
    },
    outlined: {
      border: `1px solid ${baseColor}`,
      color: textColor,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: alpha(baseColor, 0.08),
        boxShadow: theme.shadows[1],
      },
    },
    text: {
      color: textColor,
      backgroundColor:
        color === 'error'
          ? theme.palette.background.errorContainer
          : 'transparent',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor:
          color === 'error'
            ? theme.palette.background.errorContainer
            : alpha(baseColor, 0.08),
        boxShadow: theme.shadows[1],
      },
    },
  }

  return variantStyles[variant]
}

const StyledIconButton = styled(MuiIconButton, {
  shouldForwardProp: (prop) =>
    !['variant', 'icon', 'iconProps'].includes(prop as string),
})<EnhancedIconButtonProps>(
  ({ theme, color = 'default', variant = 'text', size = 'medium' }) => ({
    borderRadius: '50%',
    transition: theme.transitions.create(
      ['background-color', 'box-shadow', 'border-color', 'color'],
      {
        duration: theme.transitions.duration.short,
      },
    ),

    ...getButtonSizeStyles(size),
    ...getColorStyles(theme, color, variant),

    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      boxShadow: 'none',
    },
  }),
)

export const IconButton = React.forwardRef<
  HTMLButtonElement,
  EnhancedIconButtonProps
>((props, ref) => {
  const { icon: Icon, iconProps, children, ...otherProps } = props
  return (
    <StyledIconButton ref={ref} {...otherProps}>
      {Icon ? <Icon {...iconProps} /> : children}
    </StyledIconButton>
  )
})

IconButton.displayName = 'IconButton'
